/**
 * 企业名等价判定：用于「用户输入是否就是某个已知企业的全称」。
 *
 * 只解决**展示层等价**，不参与任何落库或对端请求：
 * - 全角 → 半角（工商登记里 `（集团）` 与 `(集团)` 两种写法都常见）
 * - 去掉全部空白（用户从 PDF / Excel 复制常带首尾与内部空白、全角空格）
 *
 * 之所以单独成模块：命中后可以跳过模糊匹配接口，属于「省一次请求」的判定依据，
 * 判定规则一旦和业务预期不符（比如误判成同义），会直接把人带到错误的企业上，
 * 因此需要可单测、可复用，而不是散落在组件里。
 */

/** 全角 ASCII（U+FF01–U+FF5E）与半角 ASCII（U+0021–U+007E）相差 0xFEE0。 */
const FULLWIDTH_OFFSET = 0xfee0;

export function normalizeCompanyName(raw: string | null | undefined): string {
  return (raw ?? '')
    .replace(/[\uff01-\uff5e]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - FULLWIDTH_OFFSET),
    )
    .replace(/\u3000/g, '') // 全角空格（不在上面的区间内）
    .replace(/\s+/g, '')
    .trim();
}

/** 参与匹配所需的最小字段集（`AnalysisRecord` 与 `EnterpriseSearchItem` 都能满足）。 */
export interface CompanyIdentityLike {
  companyName?: string | null;
  name?: string | null;
  companyId?: string | null;
  creditCode?: string | null;
  region?: string | null;
  industry?: string | null;
}

/**
 * 在一组历史分析记录里找**企业全称精确匹配**的那条。
 *
 * 只做全称精确匹配，**不做前缀/包含匹配**——模糊与否交给后端的企业搜索接口，
 * 这里只是「已经确定是同一家」才跳过它。命中多条时取第一条（列表按最近活动倒序，
 * 即最近分析过的那条）。
 */
export function findRecordByCompanyName<T extends CompanyIdentityLike>(
  records: readonly T[] | null | undefined,
  keyword: string,
): T | null {
  const target = normalizeCompanyName(keyword);
  if (!target) return null;
  for (const record of records ?? []) {
    const name = record.companyName ?? record.name ?? '';
    if (normalizeCompanyName(name) === target) return record;
  }
  return null;
}

// `CompanyIdentityLike.companyId` 等字段在此模块内不参与判定，仅用于让调用方
// 直接把命中记录透传成「已确认主体」，省去二次映射。
